/**
 * Minimap - Top-down view of the maze showing player position
 */
export class Minimap {
  constructor(mazeData, canvasId = 'minimap-canvas') {
    this.canvas = document.getElementById(canvasId);
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

    // Maze data
    this.gridSize = mazeData.gridSize || 3;
    this.cellSize = mazeData.cellSize || 18;
    this.totalSize = mazeData.totalSize || this.gridSize * this.cellSize;
    this.rooms = mazeData.rooms || [];
    this.corridors = mazeData.corridors || [];
    this.entranceRoom = mazeData.entranceRoom;
    this.exitRoom = mazeData.exitRoom;

    // Player tracking
    this.playerX = 0;
    this.playerZ = 0;
    this.playerRotation = 0;

    // Visited rooms tracking
    this.visitedRooms = new Set();

    // Display settings
    this.padding = 10;
    this.scale = 1;

    // Colors
    this.colors = {
      background: '#0a0a0f',
      room: '#1a1a2e',
      roomVisited: '#2a2a4e',
      corridor: '#252540',
      wall: '#333355',
      player: '#4a9eff',
      playerDirection: '#ffffff',
      entrance: '#4aff4a',
      exit: '#ff4a4a',
      painting: '#ffaa00'
    };

    if (this.canvas) {
      this.setupCanvas();
    }
  }

  setupCanvas() {
    // Set canvas size for crisp rendering
    const containerWidth = this.canvas.parentElement?.clientWidth || 180;
    const containerHeight = this.canvas.parentElement?.clientHeight || 180;

    // Use higher resolution for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = containerWidth * dpr;
    this.canvas.height = containerHeight * dpr;

    // Scale context for high DPI
    this.ctx.scale(dpr, dpr);

    // Calculate scale to fit maze in canvas
    const availableSize = Math.min(containerWidth, containerHeight) - this.padding * 2;
    this.scale = availableSize / this.totalSize;

    // Center offset
    this.offsetX = (containerWidth - this.totalSize * this.scale) / 2;
    this.offsetY = (containerHeight - this.totalSize * this.scale) / 2;
  }

  /**
   * Convert world coordinates to canvas coordinates
   */
  worldToCanvas(worldX, worldZ) {
    return {
      x: this.offsetX + worldX * this.scale,
      y: this.offsetY + worldZ * this.scale
    };
  }

  /**
   * Update player position and rotation
   */
  updatePlayer(x, z, rotation) {
    this.playerX = x;
    this.playerZ = z;
    this.playerRotation = rotation;

    // Check which room player is in and mark as visited
    this.checkVisitedRoom(x, z);
  }

  /**
   * Check if player is in a room and mark it as visited
   */
  checkVisitedRoom(x, z) {
    for (const room of this.rooms) {
      if (x >= room.x && x <= room.x + room.width &&
          z >= room.z && z <= room.z + room.depth) {
        this.visitedRooms.add(room.id);
        break;
      }
    }
  }

  /**
   * Draw the minimap
   */
  draw() {
    if (!this.ctx) return;

    const ctx = this.ctx;

    // Clear canvas
    ctx.fillStyle = this.colors.background;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw rooms
    this.drawRooms();

    // Draw corridors (connections between rooms)
    this.drawCorridors();

    // Draw player
    this.drawPlayer();

    // Draw border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, this.canvas.width / (window.devicePixelRatio || 1), this.canvas.height / (window.devicePixelRatio || 1));
  }

  /**
   * Draw all rooms
   */
  drawRooms() {
    const ctx = this.ctx;

    for (const room of this.rooms) {
      const topLeft = this.worldToCanvas(room.x, room.z);
      const width = room.width * this.scale;
      const height = room.depth * this.scale;

      // Room fill - brighter if visited
      const isVisited = this.visitedRooms.has(room.id);
      ctx.fillStyle = isVisited ? this.colors.roomVisited : this.colors.room;
      ctx.fillRect(topLeft.x, topLeft.y, width, height);

      // Room border
      ctx.strokeStyle = this.colors.wall;
      ctx.lineWidth = 1;
      ctx.strokeRect(topLeft.x, topLeft.y, width, height);

      // Mark entrance room
      if (room.id === this.entranceRoom) {
        ctx.fillStyle = this.colors.entrance;
        ctx.beginPath();
        ctx.arc(topLeft.x + width / 2, topLeft.y + height / 2, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Mark exit room
      if (room.id === this.exitRoom) {
        ctx.fillStyle = this.colors.exit;
        ctx.beginPath();
        ctx.arc(topLeft.x + width / 2, topLeft.y + height / 2, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw paintings as small dots (if room has paintings)
      if (room.paintings && room.paintings.length > 0) {
        ctx.fillStyle = this.colors.painting;
        const paintingSize = 2;
        room.paintings.forEach((painting, i) => {
          const px = this.worldToCanvas(painting.position.x, painting.position.z);
          ctx.fillRect(px.x - paintingSize / 2, px.y - paintingSize / 2, paintingSize, paintingSize);
        });
      }
    }
  }

  /**
   * Draw corridors between rooms
   */
  drawCorridors() {
    const ctx = this.ctx;
    ctx.strokeStyle = this.colors.corridor;
    ctx.lineWidth = 3 * this.scale;

    for (const corridor of this.corridors) {
      const room1 = this.rooms.find(r => r.id === corridor.from);
      const room2 = this.rooms.find(r => r.id === corridor.to);

      if (room1 && room2) {
        const center1 = this.worldToCanvas(
          room1.x + room1.width / 2,
          room1.z + room1.depth / 2
        );
        const center2 = this.worldToCanvas(
          room2.x + room2.width / 2,
          room2.z + room2.depth / 2
        );

        // Draw corridor line
        ctx.beginPath();
        ctx.moveTo(center1.x, center1.y);
        ctx.lineTo(center2.x, center2.y);
        ctx.stroke();
      }
    }
  }

  /**
   * Draw player position and direction indicator
   */
  drawPlayer() {
    const ctx = this.ctx;
    const pos = this.worldToCanvas(this.playerX, this.playerZ);

    // Player dot
    ctx.fillStyle = this.colors.player;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
    ctx.fill();

    // Direction indicator (triangle pointing in look direction)
    const dirLength = 8;
    const dirAngle = -this.playerRotation; // Negate for correct orientation

    ctx.fillStyle = this.colors.playerDirection;
    ctx.beginPath();

    // Triangle pointing in direction
    const tipX = pos.x + Math.sin(dirAngle) * dirLength;
    const tipY = pos.y - Math.cos(dirAngle) * dirLength;

    const baseAngle = Math.PI / 6; // 30 degrees
    const baseX1 = pos.x + Math.sin(dirAngle + Math.PI - baseAngle) * 4;
    const baseY1 = pos.y - Math.cos(dirAngle + Math.PI - baseAngle) * 4;
    const baseX2 = pos.x + Math.sin(dirAngle + Math.PI + baseAngle) * 4;
    const baseY2 = pos.y - Math.cos(dirAngle + Math.PI + baseAngle) * 4;

    ctx.moveTo(tipX, tipY);
    ctx.lineTo(baseX1, baseY1);
    ctx.lineTo(baseX2, baseY2);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * Get exploration progress (percentage of rooms visited)
   */
  getExplorationProgress() {
    if (this.rooms.length === 0) return 0;
    return (this.visitedRooms.size / this.rooms.length) * 100;
  }

  /**
   * Dispose of minimap resources
   */
  dispose() {
    this.visitedRooms.clear();
    this.rooms = [];
    this.corridors = [];
  }
}
